import type { CrowdexAssistant } from '@crowdex/agent-contracts/api';
import { Button } from '@crowdex/ui/components/button';
import {
  DesktopDropdownContent,
  DesktopDropdownItem,
  DesktopDropdownLabel,
  DesktopDropdownSubContent,
} from '@crowdex/ui/components/desktop';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@crowdex/ui/components/dialog';
import {
  DropdownMenu,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@crowdex/ui/components/dropdown-menu';
import { Input } from '@crowdex/ui/components/input';
import { showToast } from '@crowdex/ui/components/toaster';
import { cn } from '@crowdex/ui/lib/utils';
import { Archive, MoreVertical, Pencil, Settings2 } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import {
  type ConversationAction,
  type ConversationActionId,
  useConversationActions,
} from '@/components/sidebar/conversation-actions';
import { useShellNavigation } from '@/components/sidebar/shell-navigation';
import {
  PROJECT_SETTINGS_VIEW,
  useOpenAssistantPanel,
} from '@/features/assistants/assistant-panel-tabs';
import { openAssistant, workspaceDirectoryStore } from '@/lib/assistants/directory';
import { useDesktopShellStore, useDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import { matchesShortcut, shortcutLabel } from '@/lib/platform';
import type { SidebarThread } from '@/lib/sidebar-threads';
import { SHEET_ON_PHONE } from './dialog-sheet';

export {
  FIND_IN_CONVERSATION_EVENT,
  type ShareLink,
  useConversationProjectName,
  useShareLink,
} from '@/components/sidebar/conversation-actions';

/** What only the shell can do for the conversation shown. */
export type ConversationMenuActions = {
  /** Its repository's setup script and variables (D47); none without a repository. */
  onRepositorySetup?: (() => void) | undefined;
  /**
   * Brings it to a computer: on the desktop, a cloud conversation's handoff confirmation
   * (D50); on a phone, the handoff sheet (45).
   */
  onContinueLocally?: (() => void) | undefined;
};

/** An entry of a ⋮: a conversation's action, or a project's (a project's own conversation). */
export type ConversationMenuEntry = Omit<ConversationAction, 'id'> & {
  id: ConversationActionId | ProjectActionId;
};

type ProjectActionId = 'rename-project' | 'project-settings' | 'archive-project';

/** What a conversation the list does not have (not saved yet) can still do. */
const UNSAVED: ReadonlySet<ConversationMenuEntry['id']> = new Set([
  'split-view',
  'side-chat',
  'continue-locally',
  'find',
  'repository-setup',
]);

function failed(what: string) {
  return (reason: unknown) =>
    showToast({
      title: `The ${what}.`,
      ...(reason instanceof Error ? { description: reason.message } : {}),
      tone: 'error',
    });
}

/**
 * The ⋮ of the conversation shown (D6, mobile 4): the conversation actions every surface lists
 * (`conversationActions`), for the one shown, with what only the shell does (its repository's
 * setup, the handoff). Rename asks in its own dialog, Delete confirms (`dialogs`). Its branch
 * and where it runs are the header's chip and the Summary's, not entries here.
 */
export function useConversationMenu({
  actions,
  conversationId,
  surface,
}: {
  actions: ConversationMenuActions;
  conversationId: string;
  surface: 'desktop' | 'mobile';
}): { entries: ConversationMenuEntry[]; dialogs: ReactNode; run: (id: string) => void } {
  const navigation = useShellNavigation();
  // The conversation the window shows is the shell's active thread (a preview's names it apart).
  const shownId = useDesktopShellStore((state) => state.chatId);
  const threadId =
    conversationId === shownId && navigation.activeThreadId !== ''
      ? navigation.activeThreadId
      : conversationId;
  const listed = navigation.threads.find((candidate) => candidate.id === threadId);
  const thread: SidebarThread = listed ?? {
    id: threadId,
    title: 'New conversation',
    project: '',
    updatedAt: 0,
  };
  const [renaming, setRenaming] = useState(false);
  const menu = useConversationActions(thread, {
    running: thread.loading ?? false,
    surface: surface === 'desktop' ? 'desktop' : 'phone',
    onRename: () => setRenaming(true),
    shown: true,
    splitWithOthers: true,
    handlers: {
      repositorySetup: actions.onRepositorySetup,
      ...(actions.onContinueLocally === undefined
        ? {}
        : { continueLocally: actions.onContinueLocally }),
    },
  });
  const entries =
    listed === undefined ? menu.actions.filter((entry) => UNSAVED.has(entry.id)) : menu.actions;
  const dialogs =
    listed === undefined ? null : (
      <>
        <RenameDialog
          label="Conversation title"
          onClose={() => setRenaming(false)}
          onRename={(title) =>
            void navigation
              .renameConversation?.(listed, title)
              .catch(failed('conversation was not renamed'))
          }
          open={renaming}
          title={listed.title}
          heading="Rename the conversation"
        />
        {menu.confirmDelete}
      </>
    );
  return {
    entries,
    dialogs,
    run: (id) => entries.find((entry) => entry.id === id)?.run?.(),
  };
}

/**
 * An assistant's channel is the assistant (D65): its ⋮ acts on the assistant. Rename assistant,
 * Assistant settings (the panel's tab) and Archive assistant, which confirms first and which the
 * home assistant has not; an assistant is not moved, branched or deleted from here.
 */
export function useProjectMenu(
  project: CrowdexAssistant | null,
  /** The assistant's channel; null until it is first opened. */
  conversationId: string | null,
): { entries: ConversationMenuEntry[]; dialogs: ReactNode } {
  const navigation = useShellNavigation();
  const openProjectPanel = useOpenAssistantPanel();
  const store = useDesktopShellStoreApi();
  const [renaming, setRenaming] = useState(false);
  const [archiving, setArchiving] = useState(false);
  if (project === null) return { entries: [], dialogs: null };
  const shown = () => conversationId !== null && store.getState().chatId === conversationId;
  // From the sidebar's row, the project opens first: its settings are its panel's tab.
  const openSettings = () => {
    if (shown() && conversationId !== null) {
      openProjectPanel(conversationId, { view: PROJECT_SETTINGS_VIEW });
      return;
    }
    void openAssistant(project, (own) => {
      const stop = store.subscribe((state) => {
        if (state.chatId !== own) return;
        stop();
        window.clearTimeout(timer);
        openProjectPanel(own, { view: PROJECT_SETTINGS_VIEW });
      });
      const timer = window.setTimeout(stop, 10_000);
      navigation.openConversation(own);
    }).catch(failed('assistant did not open'));
  };
  const update = (input: { name: string } | { archived: boolean }) =>
    workspaceDirectoryStore().getState().updateAssistant(project.id, input);
  const entries: ConversationMenuEntry[] = [
    {
      id: 'rename-project',
      label: 'Rename assistant',
      icon: Pencil,
      run: () => setRenaming(true),
    },
    {
      id: 'project-settings',
      label: 'Assistant settings',
      icon: Settings2,
      run: openSettings,
    },
  ];
  if (project.isHome !== true)
    entries.push({
      id: 'archive-project',
      label: 'Archive assistant',
      icon: Archive,
      separated: true,
      run: () => setArchiving(true),
    });
  const dialogs = (
    <>
      <RenameDialog
        heading="Rename the assistant"
        label="Assistant name"
        onClose={() => setRenaming(false)}
        onRename={(name) => void update({ name }).catch(failed('assistant was not renamed'))}
        open={renaming}
        title={project.name}
      />
      <Dialog onOpenChange={setArchiving} open={archiving}>
        <DialogContent className={cn('sm:max-w-sm', SHEET_ON_PHONE)}>
          <DialogTitle>Archive “{project.name}”?</DialogTitle>
          <DialogDescription>
            It leaves the sidebar with its finished conversations. Search brings it back.
          </DialogDescription>
          <div className="mt-2 flex justify-end gap-2 max-md:[&>button]:h-12 max-md:[&>button]:flex-1">
            <Button onClick={() => setArchiving(false)} variant="outline">
              Cancel
            </Button>
            <Button
              onClick={() => {
                setArchiving(false);
                // Archived from the sidebar while another conversation shows, that one stays.
                const leave = shown();
                void update({ archived: true }).then(
                  () => leave && navigation.newTask(),
                  failed('assistant was not archived'),
                );
              }}
            >
              Archive assistant
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
  return { entries, dialogs };
}

function RenameDialog({
  heading,
  label,
  open,
  title,
  onClose,
  onRename,
}: {
  heading: string;
  label: string;
  open: boolean;
  title: string;
  onClose: () => void;
  onRename: (title: string) => void;
}) {
  const [value, setValue] = useState(title);
  useEffect(() => {
    if (open) setValue(title);
  }, [open, title]);
  return (
    // A bottom sheet on a phone, as the conversation's other dialogs (mobile 42–51).
    <Dialog onOpenChange={(next) => !next && onClose()} open={open}>
      <DialogContent className={cn('sm:max-w-sm', SHEET_ON_PHONE)}>
        <DialogTitle>{heading}</DialogTitle>
        <form
          className="mt-2 flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            const next = value.trim();
            if (next.length > 0 && next !== title) onRename(next);
            onClose();
          }}
        >
          <Input
            aria-label={label}
            autoFocus
            onChange={(event) => setValue(event.target.value)}
            value={value}
          />
          <div className="flex justify-end gap-2 max-md:[&>button]:h-12 max-md:[&>button]:flex-1">
            <Button onClick={onClose} type="button" variant="outline">
              Cancel
            </Button>
            <Button disabled={value.trim().length === 0} type="submit">
              Rename
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Whether a key press is typing in a field (where ⌘⌫ deletes text, not the conversation). */
function typing(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable || target.closest('input, textarea, select') !== null)
  );
}

/**
 * The shortcuts the desktop menu shows (⇧⌘L side chat, ⌘F find, F2 rename, ⌘⌫ archive), from
 * anywhere in the window but a field (F2 and ⌘⌫) or the panel (⌘F finds in its browser page).
 */
export function useConversationMenuShortcuts(
  run: (id: string) => void,
  entries: readonly ConversationMenuEntry[],
  enabled: boolean,
) {
  const current = useRef({ run, entries });
  current.current = { run, entries };
  useEffect(() => {
    if (!enabled) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      const entry = current.current.entries.find(
        (candidate) =>
          candidate.shortcut !== undefined &&
          candidate.run !== undefined &&
          matchesShortcut(event, candidate.shortcut),
      );
      if (entry === undefined) return;
      if ((entry.id === 'rename' || entry.id === 'archive') && typing(event.target)) return;
      if (
        entry.id === 'find' &&
        event.target instanceof Element &&
        event.target.closest('.desktop-side-panel') !== null
      )
        return;
      event.preventDefault();
      current.current.run(entry.id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled]);
}

/** The ⋮ of a conversation's header (D6, mobile 4). */
export function ConversationMenu({
  entries,
  surface = 'desktop',
  title,
}: {
  entries: readonly ConversationMenuEntry[];
  /** The phone's menu (4) is a sheet with bigger targets. */
  surface?: 'desktop' | 'mobile';
  title: string;
}) {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          aria-label="Conversation menu"
          size={surface === 'mobile' ? 'icon' : 'icon-sm'}
          title="More"
          variant="ghost"
        >
          <MoreVertical className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DesktopDropdownContent align="end" className="w-[17.5rem]" sideOffset={6}>
        <DesktopDropdownLabel className="type-meta truncate px-2 pt-1.5 pb-1 font-normal text-muted-foreground">
          {title}
        </DesktopDropdownLabel>
        {entries.map((entry) => (
          <ConversationMenuItem entry={entry} key={entry.id} />
        ))}
      </DesktopDropdownContent>
    </DropdownMenu>
  );
}

const ITEM = 'gap-3 [&_svg]:size-4';

function EntryText({ entry }: { entry: ConversationMenuEntry }) {
  const Icon = entry.icon;
  return (
    <>
      <span className="shrink-0 text-muted-foreground">
        <Icon />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate">{entry.label}</span>
        {entry.detail !== undefined && (
          <span className="type-caption truncate text-muted-foreground">{entry.detail}</span>
        )}
      </span>
      {entry.shortcut !== undefined && (
        <kbd className="type-caption shrink-0 rounded border border-hairline-strong px-1 font-sans text-muted-foreground">
          {shortcutLabel(entry.shortcut)}
        </kbd>
      )}
    </>
  );
}

function ConversationMenuItem({ entry }: { entry: ConversationMenuEntry }) {
  return (
    <>
      {entry.separated === true && <DropdownMenuSeparator className="mx-2 my-1 bg-border/70" />}
      {entry.submenu !== undefined ? (
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className={cn('type-ui', ITEM)}>
            <EntryText entry={entry} />
          </DropdownMenuSubTrigger>
          <DesktopDropdownSubContent className="w-64">
            {entry.submenu.map((item) => (
              <DesktopDropdownItem key={item.id} onSelect={item.run}>
                <span className="truncate">{item.label}</span>
              </DesktopDropdownItem>
            ))}
          </DesktopDropdownSubContent>
        </DropdownMenuSub>
      ) : (
        <DesktopDropdownItem
          className={ITEM}
          variant={entry.destructive === true ? 'destructive' : 'default'}
          onSelect={() => entry.run?.()}
        >
          <EntryText entry={entry} />
        </DesktopDropdownItem>
      )}
    </>
  );
}
